import type { PaymentStatus } from '@cpce/database/enums';
import type { InstalmentDraft } from '@cpce/shared';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import Decimal from 'decimal.js';
import { OptionBadge } from '@/components/Badge';
import { DatePicker } from '@/components/DatePicker';
import DecimalInput from '@/components/DecimalInput';
import { Input } from '@/components/Input';
import { PAYMENT_STATUS_OPTIONS } from '@/features/treasury/columns/bank-deposit-payments-columns';
import { formatDate } from '@/utils/date.utils';
import { formatDecimalAsPrice } from '@/utils/string.utils';

// The generated rows carry a stable id so an edit targets a row rather than its position: the
// table is filterable and sortable, and a row's index there is not its index in the échéancier.
export type PaymentInstalmentRow = InstalmentDraft & { id: string; status?: PaymentStatus };

const columnHelper = createColumnHelper<PaymentInstalmentRow>();

// `update` is what makes the rows editable: an échéancier already validated is shown with the same
// columns, read-only.
export function createPaymentInstalmentColumns(params: {
  update?: (id: string, row: PaymentInstalmentRow) => void;
  hasInstrumentNumber: boolean;
}): ColumnDef<PaymentInstalmentRow>[] {
  const { update, hasInstrumentNumber } = params;

  const instrumentNumberColumns = hasInstrumentNumber
    ? [
        columnHelper.accessor('instrumentNumber', {
          id: 'instrumentNumber',
          header: 'N° de chèque',
          meta: { filterType: 'text' },
          cell: (info) =>
            update ? (
              <Input
                value={info.row.original.instrumentNumber ?? ''}
                placeholder="Ex : 0004521"
                onChange={(event) =>
                  update(info.row.original.id, {
                    ...info.row.original,
                    instrumentNumber: event.target.value || null,
                  })
                }
              />
            ) : (
              (info.row.original.instrumentNumber ?? '-')
            ),
        }),
      ]
    : [];

  return [
    ...instrumentNumberColumns,
    columnHelper.accessor('dueDate', {
      id: 'dueDate',
      header: 'Date du règlement',
      meta: { filterType: 'numeric' },
      cell: (info) =>
        update ? (
          <DatePicker
            value={info.row.original.dueDate}
            onChange={(dueDate) => {
              if (dueDate) update(info.row.original.id, { ...info.row.original, dueDate });
            }}
          />
        ) : (
          formatDate(info.row.original.dueDate)
        ),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: 'Montant',
      meta: { filterType: 'numeric' },
      cell: (info) =>
        update ? (
          <DecimalInput
            value={info.row.original.amount}
            onlyPositive
            onChange={(amount) =>
              update(info.row.original.id, {
                ...info.row.original,
                amount: amount ?? new Decimal(0),
              })
            }
          />
        ) : (
          formatDecimalAsPrice(info.row.original.amount)
        ),
    }),
    ...(update
      ? []
      : [
          columnHelper.accessor((row) => row.status ?? null, {
            id: 'status',
            header: 'Statut',
            cell: (info) => (
              <OptionBadge options={PAYMENT_STATUS_OPTIONS} value={info.getValue()} />
            ),
            meta: { filterType: 'badge-enum', options: PAYMENT_STATUS_OPTIONS },
          }),
        ]),
  ] as ColumnDef<PaymentInstalmentRow>[];
}
